import PDFMerger from 'pdf-merger-js/browser'
import { Grid } from '@material-ui/core'
import { FunctionComponent } from 'react'
import { AiOutlineDownload } from 'react-icons/ai'
import { FiAlertCircle, FiFileText } from 'react-icons/fi'
import { registerEvent } from 'shared/services/TrackingGAService'
import { colors } from 'shared/styles/colors'
import DocumentoAnexado from './DocumentoAnexado'
import {
  Container,
  Download,
  ContainerNoContent,
  NoContent,
  Title,
  Total,
  Wrapper,
} from './styles'
import IGetAllDocumentosPdfImpl from 'modules/lotesSolicitados/domain/usecases/GetAllDocumentosPdfImpl'
import IGetDocumentoPdfImpl from 'modules/lotesSolicitados/domain/usecases/GetDocumentoPdfImpl'
import IGetDocumentoPedagioPdfImpl from 'modules/lotesSolicitados/domain/usecases/GetDocumentoPedagioPdfImpl'
import ILoteSolicitado from 'modules/lotesSolicitados/domain/entities/LoteSolicitado'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'

interface IProps {
  lote: ILoteSolicitado
  total: number
  CTE?: string[]
  CFE?: string[]
  MDFE?: string[]
  NFS?: string[]
  CartaFrete?: string[]
  ContratoFrete?: string[]
  TermoEstadia?: string[]
  ReciboPedagio?: string[]
  loading: boolean
  setLoading: (value: boolean) => void
  getAllDocumentosPdf: IGetAllDocumentosPdfImpl
  getDocumentoPdf: IGetDocumentoPdfImpl
  getDocumentoPedagioPdf: IGetDocumentoPedagioPdfImpl
}

const Documentos: FunctionComponent<IProps> = ({
  lote,
  total,
  CTE,
  CFE,
  MDFE,
  NFS,
  CartaFrete,
  ContratoFrete,
  TermoEstadia,
  ReciboPedagio,
  setLoading,
  loading,
  getAllDocumentosPdf,
  getDocumentoPdf,
  getDocumentoPedagioPdf,
}) => {
  if (total === 0)
    return (
      <ContainerNoContent>
        <FiAlertCircle size={18} color={colors.warning} />
        <NoContent>Ainda não há documentos emitidos para essa viagem</NoContent>
      </ContainerNoContent>
    )

  const handleVisualizar = async (
    documents: string[],
    documentName: string,
  ) => {
    const pdfs = []
    setLoading(true)

    for await (const doc of documents) {
      const params = doc.split('?')[1]
      const response = await getDocumentoPdf.execute(params)
      const byteCharacters = Buffer.from(response.file, 'base64')
      pdfs.push(byteCharacters)
    }

    setLoading(false)
    const merger = new PDFMerger()

    for (const file of pdfs) {
      await merger.add(file)
    }

    const mergedPdf = await merger.saveAsBlob()
    const url = URL.createObjectURL(mergedPdf)
    window.open(url, '_blank')

    registerEvent({
      categoria: 'Documento viagem',
      acao: 'Click visualizar',
      local: documentName,
    })
  }

  const handleVisualizarReciboPedagio = async (
    documents: string[],
    documentName: string,
  ) => {
    const pdfs = []
    setLoading(true)

    for await (const doc of documents) {
      const params = doc.split('?')[1]
      const response = await getDocumentoPedagioPdf.execute(params)
      const byteCharacters = Buffer.from(response.file, 'base64')
      pdfs.push(byteCharacters)
    }

    setLoading(false)
    const merger = new PDFMerger()

    for (const file of pdfs) {
      await merger.add(file)
    }

    const mergedPdf = await merger.saveAsBlob()
    const url = URL.createObjectURL(mergedPdf)
    window.open(url, '_blank')

    registerEvent({
      categoria: 'Documento viagem',
      acao: 'Click visualizar',
      local: documentName,
    })
  }

  const CTEComponent = () => {
    if (CTE?.length > 0) {
      return (
        <DocumentoAnexado
          title="DACT-e"
          subtitle={`(${CTE.length} documentos)`}
          onClick={() => {
            handleVisualizar(CTE, 'DACT-e')
          }}
        />
      )
    }
  }

  const NFSEComponent = () => {
    if (NFS?.length > 0) {
      return (
        <DocumentoAnexado
          title="NFS-e"
          subtitle={`(${NFS.length} documentos)`}
          onClick={() => {
            handleVisualizar(NFS, 'NFS-e')
          }}
        />
      )
    }
  }

  const MDFEComponent = () => {
    if (MDFE?.length > 0) {
      return (
        <DocumentoAnexado
          title="MDF-e"
          subtitle={`(${MDFE.length} documento)`}
          onClick={() => {
            handleVisualizar(MDFE, 'MDF-e')
          }}
        />
      )
    }
  }

  const TermoEstadiaComponent = () => {
    if (TermoEstadia?.length > 0) {
      return (
        <DocumentoAnexado
          title="Termo Estadia"
          subtitle={`(${TermoEstadia.length} documento)`}
          onClick={() => {
            handleVisualizar(TermoEstadia, 'Termo Estadia')
          }}
        />
      )
    }
  }

  const CFEComponent = () => {
    if (CFE?.length > 0) {
      return (
        <DocumentoAnexado
          title="CF-e"
          subtitle={`(${CFE.length} documento)`}
          onClick={() => {
            handleVisualizar(CFE, 'CF-e')
          }}
        />
      )
    }
  }
  const CartaFreteComponent = () => {
    if (CartaFrete?.length > 0) {
      return (
        <DocumentoAnexado
          title="Carta Frete"
          subtitle={`(${CartaFrete.length} documento)`}
          onClick={() => {
            handleVisualizar(CartaFrete, 'Carta Frete')
          }}
        />
      )
    }
  }

  const ContratoFreteComponent = () => {
    if (ContratoFrete?.length > 0) {
      return (
        <DocumentoAnexado
          title="Contrato Frete"
          subtitle={`(${ContratoFrete.length} documento)`}
          onClick={() => {
            handleVisualizar(ContratoFrete, 'Contrato Frete')
          }}
        />
      )
    }
  }

  const ReciboPedagioComponent = () => {
    if (ReciboPedagio?.length > 0) {
      return (
        <DocumentoAnexado
          title="Recibo Pedágio"
          subtitle={`(${ReciboPedagio.length} documento)`}
          onClick={() => {
            handleVisualizarReciboPedagio(ReciboPedagio, 'Recibo Pedágio')
          }}
        />
      )
    }
  }

  return (
    <>
      {CTEComponent()}
      {NFSEComponent()}
      {MDFEComponent()}
      {TermoEstadiaComponent()}
      {CFEComponent()}
      {CartaFreteComponent()}
      {ContratoFreteComponent()}
      {ReciboPedagioComponent()}
    </>
  )
}

const DocumentosAnexados: FunctionComponent<IProps> = ({
  lote,
  total,
  CTE,
  CFE,
  MDFE,
  NFS,
  CartaFrete,
  ContratoFrete,
  TermoEstadia,
  ReciboPedagio,
  setLoading,
  loading,
  getAllDocumentosPdf,
  getDocumentoPdf,
  getDocumentoPedagioPdf,
}) => {
  const handleDownloadAllDocumentos = async () => {
    setLoading(true)
    if (total > 0) {
      try {
        const response = await getAllDocumentosPdf.execute(
          lote.tmov_lote_conjunto_id,
        )
        const byteCharacters = Buffer.from(response.file, 'base64')
        const file = new Blob([byteCharacters], { type: 'application/pdf' })
        const url = URL.createObjectURL(file)
        window.open(url, '_blank')
        registerEvent({
          categoria: 'Documento viagem',
          acao: 'Click visualizar',
          local: 'Todos os documentos',
        })
      } catch (error) {
      } finally {
        setLoading(false)
      }
    }
  }
  return (
    <Wrapper>
      {loading && <LoadingBackdrop open={loading} color={colors.green} />}
      <Container>
        <Grid container md={12} xs={8}>
          <Grid style={{ display: 'flex' }} item xs={12} md={8}>
            <FiFileText color={colors.white} style={{ marginRight: 10 }} />
            <Title>Documentos anexados</Title>
            <Total>{total}</Total>
          </Grid>
          <Grid
            container
            xs={12}
            md={4}
            justifyContent="flex-end"
            style={{ paddingRight: 10 }}
          >
            <Download onClick={handleDownloadAllDocumentos}>
              <Title>Baixar todos</Title>
              <AiOutlineDownload color={colors.white} />
            </Download>
          </Grid>
        </Grid>
      </Container>
      <Documentos
        lote={lote}
        total={total}
        CTE={CTE}
        CFE={CFE}
        MDFE={MDFE}
        NFS={NFS}
        CartaFrete={CartaFrete}
        ContratoFrete={ContratoFrete}
        TermoEstadia={TermoEstadia}
        ReciboPedagio={ReciboPedagio}
        setLoading={setLoading}
        loading={loading}
        getAllDocumentosPdf={getAllDocumentosPdf}
        getDocumentoPdf={getDocumentoPdf}
        getDocumentoPedagioPdf={getDocumentoPedagioPdf}
      />
    </Wrapper>
  )
}

export default DocumentosAnexados
